import React, { useState, useEffect } from 'react';
import {
  Button, Menu, MenuItem, ListItemText, Typography, Box, Chip,
} from '@mui/material';
import { Lightbulb as IdeaIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface StagedIdea {
  id: string;
  content: string;
  media_urls: string[];
  platforms: string[];
  content_variants?: Record<string, string> | null;
  /** Kept from when it was last scheduled, if it was moved back to staging. */
  scheduled_for?: string | null;
  schedule_variants?: Record<string, string> | null;
  /** Server fingerprint, so saving the idea cannot overwrite a newer change. */
  version?: string;
}

/**
 * Start the composer from something already written in Idea Staging.
 *
 * The other half of staging: somewhere to put a post with no date is only useful if there is
 * a way to pull one back out. Loads the idea's text, media, platforms and any per-network
 * captions, leaving only the day and time to choose.
 *
 * Renders nothing when there are no ideas — an empty control is a question the page cannot
 * answer. A failed lookup is also silent, because a courtesy must not obstruct composing.
 */
export const IdeaPicker: React.FC<{ onPick: (idea: StagedIdea) => void }> = ({ onPick }) => {
  const { token } = useAuth();
  const [ideas, setIdeas] = useState<StagedIdea[]>([]);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/posts?status=draft&limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setIdeas(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setIdeas([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading || ideas.length === 0) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<IdeaIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Start from an idea ({ideas.length})
      </Button>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {ideas.map(idea => (
          <MenuItem
            key={idea.id}
            onClick={() => { onPick(idea); setAnchor(null); }}
            sx={{ maxWidth: 420, whiteSpace: 'normal' }}
          >
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {idea.content.slice(0, 80)}{idea.content.length > 80 ? '…' : ''}
                </Typography>
              }
              secondary={
                idea.platforms?.length ? (
                  <Box component="span" sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {idea.platforms.map(p => (
                      <Chip key={p} size="small" variant="outlined" label={getPlatformName(p)} sx={{ height: 18 }} />
                    ))}
                  </Box>
                ) : null
              }
              secondaryTypographyProps={{ component: 'span' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default IdeaPicker;
